// 3rd-party
import range from 'lodash/range';
import moment, { Moment } from 'moment'; // Import the moment typings

// Utils
import { formatMessage } from './i18n';
import { PERIODS_VALUE_CI, PERIODS_VALUE_INC, PERIODS_VALUE_MT, TIME_PERIODS } from './constants';
import { getMaxEndDate } from './dates';

// Types
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import TimePeriods from '@/types/time-periods';
import Nullable from '@/types/nullable';
import RiskPeriods from '@/types/risk-periods';
import CalendarYearPeriod from '@/types/calendar-year-period';
import Timestamp from '@/types/timestamp';

/**
 * @param n number of years to get
 * @returns {Array<number>} array of last n years
 */
export const getLastNYears = (n: number): Array<number> => {
  const year = new Date().getFullYear();
  return range(year - 1, year - n - 1);
};

/**
 * Get the time since a certain date
 * @param {Date} date Date to reference
 * @param {Number} justNowThreshold Replaces any duration under this number (milliseconds) with 'Just Now'
 * @returns {String} Time since that date (ex. '10 days ago')
 */

export const timeSince = (date: Date | Moment, justNowThreshold?: number) => {
  if (justNowThreshold && Math.abs(moment().diff(date)) < justNowThreshold) {
    return formatMessage('Common.Time.JustNow');
  }

  return moment(date).fromNow();
};

export const yearsDiff = (d1: Date | number | string, d2: Date | number | string) => {
  const date1 = new Date(d1);
  const date2 = new Date(d2);
  const years = date2.getFullYear() - date1.getFullYear();
  return years;
};

export const monthsDiff = (d1: Date | number | string, d2: Date | number | string) => {
  const date1 = new Date(d1);
  const date2 = new Date(d2);
  const years = yearsDiff(d1, d2);
  const months = years * 12 + (date2.getUTCMonth() - date1.getUTCMonth());
  return months;
};

export const weeksDiff = (d1: Date | number | string, d2: Date | number | string) => {
  const date1 = new Date(d1);
  const date2 = new Date(d2);
  return Math.round((date2.getTime() - date1.getTime()) / weeks(1));
};

export const getLastDayOfPreviousMonth = () => {
  const date = new Date(); // current date
  date.setDate(1); // going to 1st of the month
  date.setHours(-24); // going the day before
  date.setUTCHours(0, 0, 0, 0); // set utc hours so that the date starts at midnight in UTC time
  return date;
};

const ONE_MINUTE = 60 * 1000;

export const minutes = (n: number) => n * ONE_MINUTE;
export const hours = (n: number) => minutes(n * 60);
export const days = (n: number) => hours(n * 24);
export const weeks = (n: number) => days(n * 7);
export const years = (n: number) => weeks(n * 52);

/**
 * Formats a date to a user-friendly string.
 * @param date Date to be formatted
 */
export const formatEventDate = (date: Date) => {
  const today = formatMessage('Common.DateFormats.TodayWithTime');
  const otherDay = formatMessage('Common.DateFormats.DateWithTime');

  return moment(date).calendar(undefined, {
    sameDay: today,
    lastDay: otherDay,
    lastWeek: otherDay,
    sameElse: otherDay,
  });
};

/**
 * Return age based on date. If age is less than 0, return undefined.
 * @param {Date | string} date Birth Date
 * @param {string} format Format of date
 * @returns {number} Age
 */
export const getAge = (date: Date | string, format?: string, referenceDate?: Date) => {
  const reference = referenceDate ? new Date(referenceDate) : new Date();
  const birthDate = moment(date, format).toDate();

  let age = reference.getFullYear() - birthDate.getFullYear();
  const monthDiff = reference.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && reference.getDate() < birthDate.getDate())) {
    age--;
  }

  return age > 0 ? age : undefined;
};

export const getDateFromTimePeriod = (period: TimePeriods): Nullable<moment.Moment> => {
  const date = moment();

  switch (period) {
    case TIME_PERIODS['1D']:
      date.subtract(1, 'day');
      break;
    case TIME_PERIODS['1W']:
      date.subtract(1, 'week');
      break;
    case TIME_PERIODS['1M']:
      date.subtract(1, 'month');
      break;
    case TIME_PERIODS['6M']:
      date.subtract(6, 'month');
      break;
    case TIME_PERIODS['3M']:
      date.subtract(3, 'month');
      break;
    case TIME_PERIODS['1Y']:
      date.subtract(1, 'year');
      break;
    case TIME_PERIODS['2Y']:
      date.subtract(2, 'year');
      break;
    case TIME_PERIODS['3Y']:
      date.subtract(3, 'year');
      break;
    case TIME_PERIODS['4Y']:
      date.subtract(4, 'year');
      break;
    case TIME_PERIODS['5Y']:
      date.subtract(5, 'year');
      break;
    case TIME_PERIODS['10Y']:
      date.subtract(10, 'year');
      break;
    case TIME_PERIODS.YT:
      date.startOf('year');
      break;
    case TIME_PERIODS.MT:
      return date.subtract(1, 'month').endOf('month');
    case TIME_PERIODS.INC:
    case TIME_PERIODS.CI:
    default:
      return null;
  }
  return date;
};

/**
 * Find the time that has elapsed in milliseconds from an initial time until now.
 * @param start Start measuring from
 * @param end End measuring from
 * @returns {Timestamp} Timestamp in milliseconds that has elapsed
 */
export const getTimeElapsed = (start?: Timestamp, end?: Timestamp): Timestamp => {
  if (!start) return 0;
  return (end || Date.now()) - start;
};

export const monthsToPeriod = (months: Nullable<number>, monthEndDataOption?: PERIOD_END_DATA_OPTIONS) => {
  switch (months) {
    case 120:
      return TIME_PERIODS['10Y'];
    case 60:
      return TIME_PERIODS['5Y'];
    case 48:
      return TIME_PERIODS['4Y'];
    case 36:
      return TIME_PERIODS['3Y'];
    case 24:
      return TIME_PERIODS['2Y'];
    case 12:
      return TIME_PERIODS['1Y'];
    case getMaxEndDate(monthEndDataOption).getMonth():
      return TIME_PERIODS.YT;
    case 1:
      return TIME_PERIODS['1M'];
    case 6:
      return TIME_PERIODS['6M'];
    case 3:
      return TIME_PERIODS['3M'];
    // Will use -1 as the Month End Period flag
    case PERIODS_VALUE_MT:
      return TIME_PERIODS.MT;
    case PERIODS_VALUE_CI:
      return TIME_PERIODS.CI;
    case PERIODS_VALUE_INC:
      return TIME_PERIODS.INC;
    default:
      return TIME_PERIODS.INC;
  }
};

export const periodToMonths = (period: RiskPeriods | TimePeriods) => {
  switch (period) {
    case TIME_PERIODS['10Y']:
      return 120;
    case TIME_PERIODS['5Y']:
      return 60;
    case TIME_PERIODS['4Y']:
      return 48;
    case TIME_PERIODS['3Y']:
      return 36;
    case TIME_PERIODS['2Y']:
      return 24;
    case TIME_PERIODS['1Y']:
      return 12;
    case TIME_PERIODS['6M']:
      return 6;
    case TIME_PERIODS['3M']:
      return 3;
    case TIME_PERIODS['1M']:
      return 1;
    case TIME_PERIODS.YT:
      return new Date().getMonth();
    case TIME_PERIODS.MT:
      return getLastDayOfPreviousMonth().getMonth();
    case TIME_PERIODS.CI:
      return PERIODS_VALUE_CI;
    case TIME_PERIODS.INC:
      return PERIODS_VALUE_INC;
    default:
      return null;
  }
};

export const periodToYear = (period: RiskPeriods | CalendarYearPeriod | TimePeriods) => {
  switch (period) {
    case TIME_PERIODS['10Y']:
      return 10;
    case TIME_PERIODS['5Y']:
      return 5;
    case TIME_PERIODS['4Y']:
      return 4;
    case TIME_PERIODS['3Y']:
      return 3;
    case TIME_PERIODS['2Y']:
      return 2;
    case TIME_PERIODS['1Y']:
      return 1;
    default:
      return 0;
  }
};

/**
 * Get the number of days between 2 dates.
 * @param startDate Start date
 * @param endDate End date
 * @returns {number} number of days between 2 dates
 */
export const daysBetween = (startDate: Date, endDate: Date): number => {
  const date = moment(startDate).utc().startOf('day');
  const currentDate = moment(endDate).utc().startOf('day');
  const diff = moment.duration(currentDate.diff(date));
  return diff.asDays();
};

export const getEndOfMonthTimestamp = (date: Date) => {
  const endOfMonth = new Date(date.getUTCFullYear(), date.getUTCMonth() + 1, 0);
  endOfMonth.setUTCHours(0, 0, 0, 0);
  return endOfMonth.getTime();
};

export const MAX_TIMESTAMP = 8640000000000000; // Fri Sep 12 275760 20:00:00 GMT-0400
export const MIN_TIMESTAMP = -8640000000000000; // Mon Apr 19 -271821 19:03:58 GMT-0456 (Eastern Daylight Time)
